import { nanoid } from 'nanoid';
import { useCallback, useState } from 'react';

import { UNIQUE_ID_SIZE } from 'ab-common/constants/value';

import type { ADDITIONAL_OPTIONS } from '../types';
import { ReportTypeViewTableAdditionalFilterType, type ReportTypeViewTableAdditionalFilter } from '../types';

const _filterEmptyAdditionalFilters = (_filter: ReportTypeViewTableAdditionalFilter) => _filter.value !== null;

export const useReportTypeViewAdditionalFields = (defaultValue?: ReportTypeViewTableAdditionalFilter[]) => {
	const [additionalFilters, setAdditionalFilters] = useState<ReportTypeViewTableAdditionalFilter[]>(defaultValue ?? []);
	const [completeAdditionalFilters, setCompleteAdditionalFilters] = useState<ReportTypeViewTableAdditionalFilter[]>(defaultValue ?? []);

	const editFilter = useCallback((id: string | number, newValue: ReportTypeViewTableAdditionalFilter) => {
		const newFilters = additionalFilters.map((_filter) => (_filter.id === id ? newValue : _filter));
		setAdditionalFilters(newFilters);
		setCompleteAdditionalFilters(newFilters.filter(_filterEmptyAdditionalFilters));
	}, [additionalFilters]);

	const removeFilter = useCallback((id: string | number) => {
		const newFilters = additionalFilters.filter((_filter) => _filter.id !== id);
		setAdditionalFilters(newFilters);
		setCompleteAdditionalFilters(newFilters.filter(_filterEmptyAdditionalFilters));
	}, [additionalFilters]);

	const resetAdditionalFilters = useCallback(() => {
		setAdditionalFilters([]);
		setCompleteAdditionalFilters([]);
	}, []);

	const bulkEditAdditionalFilters = useCallback((newAdditionalFilters: ReportTypeViewTableAdditionalFilter[]) => {
		setAdditionalFilters(newAdditionalFilters);
		setCompleteAdditionalFilters(newAdditionalFilters.filter(_filterEmptyAdditionalFilters));
	}, []);

	const addAdditionalFilter = useCallback((option: typeof ADDITIONAL_OPTIONS[number]) => {
		let newField: ReportTypeViewTableAdditionalFilter;
		const id = nanoid(UNIQUE_ID_SIZE);
		switch (option.id) {
			case ReportTypeViewTableAdditionalFilterType.FIELD: {
				newField = {
					id,
					type: ReportTypeViewTableAdditionalFilterType.FIELD,
					value: null,
				};
				break;
			}
			case ReportTypeViewTableAdditionalFilterType.JOB: {
				newField = {
					id,
					type: ReportTypeViewTableAdditionalFilterType.JOB,
					value: null,
				};
				break;
			}
			default: {
				newField = {
					id,
					type: ReportTypeViewTableAdditionalFilterType.FIELD,
					value: null,
				};
				break;
			}
		}
		setAdditionalFilters([...additionalFilters, newField]);
	}, [additionalFilters]);

	return {
		additionalFilters,
		completeAdditionalFilters,
		resetAdditionalFilters,
		editFilter,
		addAdditionalFilter,
		removeFilter,
		bulkEditAdditionalFilters,
	};
};

